import { Form, FormProps } from "@/components/form";
import { localNow } from "@/shared/local-now";
import { z } from "zod/v4";
import { getArticulo } from "../../articulos/api/get-articulo";
import { getAllArticulos } from "../../articulos/api/get-all-articulos";
import { getDeposito } from "../../depositos/api/get-deposito";
import { getAllDepositos } from "../../depositos/api/get-all-depositos";
import DepositoManualSearch from "../../depositos/components/deposito-manual-search";
import ArticuloMovimientoArticuloManualSearch from "./_shared/articulo-movimiento-articulo-manual-search";
import { useDepositoContext } from "../../deposito-actual/contexts/deposito-context";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { Permiso } from "@/shared/types/permiso";
import { ArticuloMovimientoTipo } from "@/shared/types/articulo-movimiento";
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import { Chip, Stack } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from "react";
import { useStore } from "@tanstack/react-form";
import { getArticulosStockPorDeposito } from "../../articulos/api/get-articulos-stock-por-deposito";

export const articuloMovimientoFormSchema = z.object({
    fecha: z.iso.datetime({ local: true, message: "Este campo es requerido" }).default(localNow()),
    tipo: z.number({ message: "Este campo es requerido" }),
    descripcion: z.string(),
    articuloId: z.string().nonempty("Este campo es requerido"),
    cantidad: z.number({ message: "Este campo es requerido" }),
    depositoId: z.string().optional(),
    stockActual: z.number().optional(),
})

export default function ArticuloMovimientoForm({
    form,
    ...props
}: FormProps<z.input<typeof articuloMovimientoFormSchema>>) {
    const openModal = useOpenModal();
    const { depositoData } = useDepositoContext();
    const hasAsignarDepositos = useHasPermisos(Permiso.AsignarDepositos);

    // Inicializar el depósito del movimiento con el depósito asignado (si no hay uno elegido).
    useEffect(() => {
        const current = form.getFieldValue("depositoId");
        if (!current && depositoData?.id) {
            form.setFieldValue("depositoId", depositoData.id);
        }
    }, [depositoData?.id]);

    // Mostrar el stock actual del artículo seleccionado, scopeado al depósito del movimiento.
    const articuloId = useStore(form.store, s => (s.values as any).articuloId as string | undefined);
    const depositoId = useStore(form.store, s => (s.values as any).depositoId as string | undefined);
    const tipo = useStore(form.store, s => (s.values as any).tipo as number | undefined);
    const cantidad = useStore(form.store, s => (s.values as any).cantidad as number | undefined);
    const [stockActual, setStockActual] = useState<number>();

    const stockInsuficiente = stockActual != null && tipo === ArticuloMovimientoTipo.Salida
        && cantidad != null && cantidad > stockActual;

    useEffect(() => {
        setStockActual(undefined);
        form.setFieldValue("stockActual", undefined);
        if (!articuloId || !depositoId) return;

        let cancelled = false;
        (async () => {
            const saldos = await getArticulosStockPorDeposito([articuloId], depositoId);
            if (cancelled) return;
            setStockActual(saldos[articuloId]);
            form.setFieldValue("stockActual", saldos[articuloId]);
        })();

        return () => { cancelled = true; };
    }, [articuloId, depositoId]);

    return <Form gap={3} form={form} {...props}>
        <Form.DatePicker name="fecha" label="Fecha" />
        <Form.Select name="tipo" label="Tipo">
            <Form.Select.Option value={ArticuloMovimientoTipo.Entrada}>Entrada</Form.Select.Option>
            <Form.Select.Option value={ArticuloMovimientoTipo.Salida}>Salida</Form.Select.Option>
        </Form.Select>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Stack flex={1}>
                <Form.ModelComboBox name="articuloId" label="Artículo" get={getArticulo} getAll={getAllArticulos} />
            </Stack>
            <Form.IconButton onClick={() => openModal("searchArticulo")}><SearchIcon /></Form.IconButton>
        </Stack>
        {stockActual != null && <Stack direction={"row"}>
            <Chip
                size="small"
                variant="outlined"
                color={stockInsuficiente ? "error" : "info"}
                label={`Stock actual: ${stockActual}`}
                sx={{ borderRadius: 1 }}
            />
        </Stack>}
        <Stack direction={"row"} gap={1}>
            <Stack flex={1}>
                <Form.ModelComboBox name="depositoId" label="Depósito" get={getDeposito} getAll={getAllDepositos} disabled={!hasAsignarDepositos} />
            </Stack>
            <Form.IconButton disabled={!hasAsignarDepositos} onClick={() => openModal("searchDeposito")}><SearchIcon /></Form.IconButton>
        </Stack>
        <Form.NumberField name="cantidad" label="Cantidad" decimalScale={0} />
        <Form.TextField name="descripcion" label="Motivo" multiline rows={3} />
        <Form.SubmitButton>Guardar</Form.SubmitButton>

        <Dialog code="searchDeposito" size="lg">
            <ModalHeader>Buscar Depósito</ModalHeader>
            <ModalContextExtractor>
                {({ handleClose }) => <DepositoManualSearch
                    padding={3}
                    onDepositoSelect={deposito => {
                        form.setFieldValue("depositoId", deposito.id);
                        handleClose();
                    }}
                />}
            </ModalContextExtractor>
        </Dialog>

        <Dialog code="searchArticulo" size="lg">
            <ModalHeader>Buscar Artículo</ModalHeader>
            <ModalContextExtractor>
                {({ handleClose }) => <ArticuloMovimientoArticuloManualSearch
                    padding={3}
                    onArticuloSelect={articulo => {
                        form.setFieldValue("articuloId", articulo.id);
                        handleClose();
                    }}
                />}
            </ModalContextExtractor>
        </Dialog>
    </Form>
}